/**
 * TemplatePicker — modal for choosing a dashboard template on first use.
 */

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
} from '@/components/ui'
import { DASHBOARD_TEMPLATES } from './dashboardTemplates'
import type { DashboardTemplate } from '@/types/dashboard'
import {
  LayoutDashboard,
  Search,
  Target,
  Briefcase,
  Plus,
} from 'lucide-react'

const ICON_MAP: Record<string, any> = {
  LayoutDashboard,
  Search,
  Target,
  Briefcase,
}

interface TemplatePickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelectTemplate: (template: DashboardTemplate) => void
  onCreateBlank: () => void
}

export function TemplatePicker({ open, onOpenChange, onSelectTemplate, onCreateBlank }: TemplatePickerProps) {
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="lg">
        <ModalHeader>
          <ModalTitle>Create Your Dashboard</ModalTitle>
          <ModalDescription>
            Start with a template or build from scratch
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DASHBOARD_TEMPLATES.map((template) => {
              const Icon = ICON_MAP[template.icon] || LayoutDashboard
              return (
                <button
                  key={template.id}
                  onClick={() => {
                    onSelectTemplate(template)
                    onOpenChange(false)
                  }}
                  className="flex flex-col items-start p-4 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
                >
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors mb-3">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-sm font-semibold text-foreground">{template.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                  <p className="text-xs text-primary mt-2">{template.widgets.length} widgets</p>
                </button>
              )
            })}

            {/* Blank dashboard option */}
            <button
              onClick={() => {
                onCreateBlank()
                onOpenChange(false)
              }}
              className="flex flex-col items-start p-4 rounded-xl border-2 border-dashed border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-left group"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors mb-3">
                <Plus className="w-5 h-5 text-muted-foreground group-hover:text-primary" />
              </div>
              <p className="text-sm font-semibold text-foreground">Blank Dashboard</p>
              <p className="text-xs text-muted-foreground mt-1">Start from scratch and add widgets manually</p>
            </button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
